/**
 * Key-value storage port — localStorage implementation with JSON serialization.
 *
 * The API stays async so shared callers behave identically on both platforms.
 * Every access is guarded: localStorage throws in private-mode Safari and when
 * the quota is exceeded.
 */
import type { KeyValueStorage } from './storageTypes';

const backing = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

const setItem = async (key: string, value: unknown): Promise<void> => {
  try {
    const serialized =
      typeof value === 'object' ? JSON.stringify(value) : String(value);
    backing()?.setItem(key, serialized);
  } catch (error) {
    console.warn('[Storage] setItem error:', error);
  }
};

const getItem = async <T = unknown>(
  key: string,
  fallback: T | null = null,
): Promise<T | null> => {
  try {
    const raw = backing()?.getItem(key);
    if (raw == null) {
      return fallback;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return raw as unknown as T;
    }
  } catch (error) {
    console.warn('[Storage] getItem error:', error);
    return fallback;
  }
};

const removeItem = async (key: string): Promise<void> => {
  try {
    backing()?.removeItem(key);
  } catch (error) {
    console.warn('[Storage] removeItem error:', error);
  }
};

const clearAll = async (): Promise<void> => {
  try {
    backing()?.clear();
  } catch (error) {
    console.warn('[Storage] clearAll error:', error);
  }
};

const multiGet = async (keys: string[]): Promise<Record<string, unknown>> => {
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    result[key] = await getItem(key);
  }
  return result;
};

export const storage: KeyValueStorage = {
  setItem,
  getItem,
  removeItem,
  clearAll,
  multiGet,
};
